import { createSelector } from 'reselect'
import { StoreDispatch } from 'interfaces/component'
import * as sortBy from 'lodash/sortBy'
import * as sumBy from 'lodash/sumBy'
import { formatPercent, roundPercent } from 'helpers/formatHelpers'
import CategoriesModule, {
  CategoriesModuleStateProps,
  CategoriesModuleDispatchProps,
  CategoriesModuleOwnProps,
} from 'components/features/pfm/CategoriesModule/CategoriesModule'
import { setCategoriesVisibilityActionCreator } from 'actions/moneyflowActions'
import { MoneyflowType, MoneyflowBreakdownItem } from 'interfaces/moneyflow'
import { CollectionCategory, CategoryId, Category } from 'interfaces/meta'
import { setFilterCategoryActionCreator } from 'actions/globalActions'
import { COLOR_SET, resolveCategory } from 'helpers/categoriesHelpers'
import { PEER_GROUP_COLORS } from 'constants/peergroups'
import { getItemsFromCollection, getItemsByKey, removeByKeyFromCollection } from 'helpers/normalizeHelpers'
import { selectedRelationSelector } from 'selectors/peergroupSelectors'
import { categoriesMoneyflowBreakdownSelector, metaMoneyflowCategoriesSelector, categoriesMoneyflowAppSelector } from 'selectors/moneyflowSelectors'

import { TimeframeTyped } from 'interfaces/global'
import { Guards } from 'interfaces/guards'
import { connectShadedLoadable, createParallelGuard } from 'helpers/loadableHelpers'
import { peergroupsGuard } from 'guards/globalGuards'
import { memberMoneyflowBreakdownGuard, peergroupsMoneyflowBreakdownGuard } from 'guards/moneyflowGuards'
import { ALL_CATEGORY_ID, TRANSFER_CATEGORY_ID } from 'constants/meta'
import { highlightCategoryActionCreator } from 'actions/globalActions'
import { createForgottenGuardError } from 'helpers/errorHelpers'
import { isMobileSelector } from 'selectors/viewportSelectors'
import { isAllCategorySelectedSelector, createOwnPropsSelector } from 'selectors/globalSelectors'
import { trackUIEventAction } from 'actions/analyticsActions'
import { withComparisonSelector } from 'components/features/pageFilters/selectors'

export type MoneyflowCategoriesModuleOwnProps =
& CategoriesModuleOwnProps
& TimeframeTyped
& {
  type: MoneyflowType
}

/**
 * Return order by first breakdown without lack of data
 * or if each breakdown with lack of data
 * return order by first breakdown
 */
const categoriesMoneyflowOrderSelector = createSelector(
  metaMoneyflowCategoriesSelector,
  categoriesMoneyflowBreakdownSelector,
  (categories, breakdowns) => {
    if (!breakdowns) {
      return null
    }

    const iteraties = breakdowns
      .map((breakdown): Record<CategoryId, number> => {
        // build relative values map
        const totalAmount = sumBy(breakdown.allKeys, (id) => breakdown.byKey[id].amount)

        return breakdown.allKeys.reduce((result, categoryId) => {
          const categoryAmount = breakdown.byKey[categoryId].amount
          const relativeAmount = totalAmount === 0 || categoryAmount === 0
            ? 0
            : roundPercent(categoryAmount / totalAmount, 'one')

          return {
            ...result,
            [categoryId]: relativeAmount,
          }
        }, {})
      })
      .map((breakdown) => (id: CategoryId) => -breakdown[id])

    const categoryIds = getItemsFromCollection(removeByKeyFromCollection<Category, 'id'>(categories, ALL_CATEGORY_ID, TRANSFER_CATEGORY_ID))
      .map((category) => category.id)
    const order = sortBy(categoryIds, ...iteraties)

    return order
  }
)

/**
 * Covers cases when breakdown if isBreakdownEmpty
 * But (CRUTCH) we fill zeros in epic
 */
const isAnyNonZeroAmount = (breakdown: MoneyflowBreakdownItem[]) =>
  !breakdown.every(({ amount }) => amount === 0)

const createSectionBuilderProps = (
  categories: CollectionCategory,
  highlightedId: CategoryId,
  selectedId: CategoryId
) => (
  breakdown: MoneyflowBreakdownItem[],
  colorSet: string[],
  fontColorSet: string[],
  fontColor: string
) => {
  let totalAmount = 0
  const normalizedBreakdowns = breakdown.map((breakdownItem) => {
    const amount = breakdownItem.amount < 0 ? 0 : breakdownItem.amount
    totalAmount += amount

    return {
      ...breakdownItem,
      amount,
    }
  })

  const isAnyNonZero = isAnyNonZeroAmount(normalizedBreakdowns)

  const items = normalizedBreakdowns
    .map(({ amount, category }) => ({
      id: category,
      amount,
    }))

  const isShaded = (id: CategoryId) =>
    (selectedId !== ALL_CATEGORY_ID || highlightedId !== ALL_CATEGORY_ID) && selectedId !== id && highlightedId !== id

  const chart = isAnyNonZero
    ? items.map(({ id, amount }, idx) => {
      const ratio = amount / totalAmount

      return {
        id,
        value: Math.round(ratio * 1000),
        title: categories.byKey[id].value,
        subtitle: formatPercent(ratio, 'one'),
        color: colorSet[idx],
        fontColor: fontColorSet[idx],
        tooltipFontColor: fontColor,
        isHighlighted: highlightedId === id || selectedId === id,
        isSelected: selectedId === id,
        isShaded: isShaded(id),
      }
    })
    : []

  const list = {
    fontColor: fontColor,
    items: items.map(({ id, amount }, idx) => {
      const shownValue = isAnyNonZero
        ? formatPercent(amount / totalAmount, 'one')
        : '0%'

      return {
        id,
        categoryName: categories.byKey[id].value,
        value: shownValue,
        color: colorSet[idx],
        isSelected: selectedId === id,
        isShaded: isShaded(id),
      }
    }),
  }

  return {
    mainColor: colorSet[0],
    mainFontColor: fontColorSet[0],
    total: totalAmount,
    chart,
    list,
  }
}

const createMapStateToProps = () => createSelector(
  createOwnPropsSelector<MoneyflowCategoriesModuleOwnProps>(),
  metaMoneyflowCategoriesSelector,
  categoriesMoneyflowBreakdownSelector,
  categoriesMoneyflowOrderSelector,
  categoriesMoneyflowAppSelector,
  selectedRelationSelector,
  isAllCategorySelectedSelector,
  isMobileSelector,
  withComparisonSelector,
  (ownProps, categories, breakdowns, order, categoriesState, selectedRelation, isAllCategorySelected, isMobile, withComparison): CategoriesModuleStateProps => {
    if (!breakdowns) {
      throw createForgottenGuardError('MoneyflowCategoriesModuleContainer', 'memberMoneyflowBreakdownGuard', 'peergroupsMoneyflowBreakdownGuard')
    }
    if (!order) {
      throw createForgottenGuardError('MoneyflowCategoriesModuleContainer', 'peerGroupGuard')
    }

    const { selectedId, highlightedId, isExpanded } = categoriesState

    const [
      memberBreakdownCollection,
      peersBreakdownCollection,
      otherBreakdownCollection,
    ] = breakdowns

    const memberBreakdown = getItemsByKey(memberBreakdownCollection.byKey, order)
    const peersBreakdown = getItemsByKey(peersBreakdownCollection.byKey, order)
    const otherBreakdown = getItemsByKey(otherBreakdownCollection.byKey, order)

    const builder = createSectionBuilderProps(categories, highlightedId, selectedId)

    return {
      isMobile,
      type: ownProps.type,
      withList: true,
      isExpanded,
      selectedPeergroupName: selectedRelation ? selectedRelation.name : null,
      selectedId,
      isAllCategorySelected,
      member: builder(
        memberBreakdown,
        COLOR_SET.member,
        COLOR_SET.memberText,
        PEER_GROUP_COLORS.member
      ),
      peers: builder(
        peersBreakdown,
        COLOR_SET.peers,
        COLOR_SET.peersText,
        PEER_GROUP_COLORS.peers
      ),
      others: builder(
        otherBreakdown,
        COLOR_SET.others,
        COLOR_SET.othersText,
        PEER_GROUP_COLORS.others
      ),
      withComparison,
    }
  }
)

const mapDispatchToProps = (dispatch: StoreDispatch, { type }: MoneyflowCategoriesModuleOwnProps): CategoriesModuleDispatchProps => ({
  highlightCategory: (category) => dispatch(
    highlightCategoryActionCreator(type, resolveCategory(category, false))
  ),
  setFilteredCategory: (category) => dispatch(
    setFilterCategoryActionCreator(type, resolveCategory(category, false))
  ),
  setExpanded: (isVisible) => dispatch(
    setCategoriesVisibilityActionCreator(type, isVisible)
  ),
  trackUIEvent: (event) => dispatch(trackUIEventAction(event)),
})

const moneyflowCategoriesGuards: Guards<MoneyflowCategoriesModuleOwnProps> = [
  peergroupsGuard,
  createParallelGuard(memberMoneyflowBreakdownGuard, peergroupsMoneyflowBreakdownGuard),
]

const container = connectShadedLoadable<
  CategoriesModuleStateProps,
  CategoriesModuleDispatchProps,
  CategoriesModuleOwnProps,
  MoneyflowCategoriesModuleOwnProps
>(createMapStateToProps, mapDispatchToProps, moneyflowCategoriesGuards)(CategoriesModule)
export default container
